import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import SignupLayout from './SignupLayout'
import { writeDraft } from './signupState'

/**
 * SignupStart — Screen 1 of /signup (persona picker).
 *
 * Light theme. Typography-only persona rows. No icons, no colored
 * squares. Premium-SaaS energy (Stripe Atlas / Linear / Cal.com).
 */

const PERSONAS = [
  {
    id: 'community',
    title: 'Organizer',
    description: 'Pickup games & leagues',
  },
  {
    id: 'academy',
    title: 'Coach',
    description: 'Club or academy',
  },
  {
    id: 'venue',
    title: 'Venue Owner',
    description: 'Courts, pitches, facilities',
  },
]

export default function SignupStart() {
  const navigate = useNavigate()
  const [picking, setPicking] = useState(null)

  const handleSelect = (id) => {
    if (picking) return
    setPicking(id)

    writeDraft({ persona: id, signupStep: 1 })

    // All personas now route through /signup/account (SSO) first.
    // SignupAuth.advance() then dispatches by persona to /signup/community,
    // /signup/academy/book, or /signup/venue/book.
    setTimeout(() => navigate('/signup/account'), 140)
  }

  // Mobile-pinned secondary entry point. Rendered by SignupLayout
  // below the centered body on viewports < md; desktop falls back
  // to the inline copy further down in `children`.
  const mobileLogIn = (
    <p className="text-center text-[14px] text-[var(--signup-text-secondary)]">
      Already on Statbox?{' '}
      <Link
        to="/login"
        className="inline-flex items-center min-h-[44px] px-2 -my-2 text-[var(--signup-accent)] font-medium hover:underline underline-offset-4 motion-safe:transition-colors motion-safe:duration-150"
      >
        Log in
      </Link>
    </p>
  )

  return (
    <SignupLayout
      showBack
      showLogin={false}
      centerOnMobile
      mobileBottomSlot={mobileLogIn}
    >
      {/* Headline — ellipsis is part of the typography, not a colored accent */}
      <h1
        className="font-semibold text-[var(--signup-text-primary)] text-[40px] sm:text-[44px] leading-[1.1]"
        style={{ letterSpacing: '-0.01em' }}
      >
        I'm a…
      </h1>
      <p className="mt-3 text-[16px] md:text-[15px] text-[var(--signup-text-secondary)] leading-[1.5]">
        Pick your role.
      </p>

      {/* Persona rows — top + between + bottom hairlines for a clean table feel */}
      <div className="mt-8 md:mt-10 border-t border-[var(--signup-border-subtle)]">
        {PERSONAS.map((p) => {
          const isPicking = picking === p.id
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => handleSelect(p.id)}
              disabled={!!picking && !isPicking}
              aria-label={`${p.title} — ${p.description}`}
              className="group relative flex w-full items-center justify-between gap-4 border-b border-[var(--signup-border-subtle)] py-7 px-1 text-left rounded-sm hover:bg-[var(--signup-bg-subtle)] disabled:opacity-40 focus:outline-none focus-visible:bg-[var(--signup-bg-subtle)] focus-visible:ring-1 focus-visible:ring-[var(--signup-accent)] motion-safe:transition-colors motion-safe:duration-150"
            >
              <div className="min-w-0">
                <div
                  className="text-[20px] sm:text-[22px] font-medium text-[var(--signup-text-primary)] leading-tight"
                  style={{ letterSpacing: '-0.012em' }}
                >
                  {p.title}
                </div>
                <div className="mt-1 text-[14px] text-[var(--signup-text-secondary)] leading-snug">
                  {p.description}
                </div>
              </div>
              <ArrowRight
                size={20}
                className={`shrink-0 text-[var(--signup-text-tertiary)] motion-safe:transition-transform motion-safe:duration-200 ${
                  isPicking
                    ? 'translate-x-1 text-[var(--signup-text-primary)]'
                    : 'group-hover:translate-x-1 group-hover:text-[var(--signup-text-primary)]'
                }`}
                style={{ transitionTimingFunction: 'var(--ease-out-expo)' }}
                aria-hidden
              />
            </button>
          )
        })}
      </div>

      {/* Desktop-only inline log-in. Mobile uses the pinned slot above. */}
      <p className="hidden md:block mt-10 text-[13px] text-[var(--signup-text-tertiary)]">
        Already on Statbox?{' '}
        <Link
          to="/login"
          className="text-[var(--signup-accent)] font-medium hover:underline underline-offset-4 motion-safe:transition-colors motion-safe:duration-150"
        >
          Log in
        </Link>
      </p>
    </SignupLayout>
  )
}
